import React, { useCallback, useEffect, useState } from "react";
import { createUser, getUsers } from "../../actions/studensts";
import { deleteUser as apiDeleteUser, updateUserRole as apiUpdateUserRole, updateUserPassword as apiUpdateUserPassword } from "../../api/index";
import AlertSuccess from "../../components/AlertSuccess";
import Loading from "../../components/Loading";
import UNAUTHORIZED from "../UNAUTHORIZED";
import { FiEdit2, FiTrash2, FiUserPlus, FiX, FiCheck, FiUsers, FiShield, FiLock } from "react-icons/fi";

const ROLES = ["admin", "auditor", "agent", "moderator"];

function AddUser() {
  const [userData, setUserData] = useState({
    username: "",
    role: "agent",
    password: "",
  });
  const [users, setUsers] = useState([]);
  const [alert, setAlert] = useState({ show: false, text: "" });
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState("");
  const [editingPassword, setEditingPassword] = useState(null);
  const [editPassword, setEditPassword] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const userProfile = JSON.parse(localStorage.getItem("profile")).value[0];
  const user = userProfile.role;

  const showAlertMsg = (text) => {
    setAlert({ show: true, text });
    setTimeout(() => setAlert({ show: false, text: "" }), 2500);
  };

  const getAllUsers = async () => {
    const { data } = await getUsers();
    if (data) setUsers(data.users);
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user '${username}'?`)) return;
    setLoading(true);
    try {
      await apiDeleteUser(username);
      await getAllUsers();
      showAlertMsg("User deleted successfully");
    } catch (err) {
      alert("Failed to delete user.");
    }
    setLoading(false);
  };

  const handleEditStart = (u) => {
    setEditingUser(u.username);
    setEditRole(u.role);
    setEditingPassword(null);
  };

  const handlePasswordStart = (u) => {
    setEditingPassword(u.username);
    setEditPassword("");
    setEditingUser(null);
  };

  const handlePasswordSave = async (username) => {
    if (!editPassword.trim()) return;
    setLoading(true);
    try {
      await apiUpdateUserPassword(username, editPassword);
      setEditingPassword(null);
      setEditPassword("");
      showAlertMsg("Password updated");
    } catch (err) {
      alert("Failed to update password.");
    }
    setLoading(false);
  };

  const handleEditSave = async (username) => {
    setLoading(true);
    try {
      await apiUpdateUserRole(username, editRole);
      setEditingUser(null);
      await getAllUsers();
      showAlertMsg("User role updated");
    } catch (err) {
      alert("Failed to update user role.");
    }
    setLoading(false);
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!userData.username || !userData.password) return;
      setLoading(true);
      const data = await createUser(userData);
      if (data) {
        showAlertMsg("New user added successfully");
        setUserData({ username: "", role: "agent", password: "" });
        setShowAddForm(false);
        await getAllUsers();
      }
      setLoading(false);
    },
    [userData],
  );

  useEffect(() => {
    getAllUsers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  const roleColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-100 text-red-700 border-red-200";
      case "auditor": return "bg-blue-100 text-blue-700 border-blue-200";
      case "agent": return "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "moderator": return "bg-purple-100 text-purple-700 border-purple-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  return (
    <div>
      {user !== "admin" ? (
        <UNAUTHORIZED />
      ) : (
        <div className="min-h-screen px-4 sm:px-8 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <FiUsers className="text-indigo-400" /> Manage Users
              </h1>
              <p className="text-slate-400 text-sm mt-1">{users.length} registered users</p>
            </div>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-lg"
            >
              {showAddForm ? <FiX /> : <FiUserPlus />}
              {showAddForm ? "Cancel" : "Add User"}
            </button>
          </div>

          {/* Alert */}
          {alert.show && (
            <div className="mb-6">
              <AlertSuccess text={alert.text} />
            </div>
          )}

          {/* Add User Form */}
          {showAddForm && (
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-6 mb-8 shadow">
              <h2 className="text-white text-lg font-semibold mb-4 flex items-center gap-2">
                <FiUserPlus className="text-indigo-400" /> New User
              </h2>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-1 block">Username</label>
                  <input
                    name="username"
                    value={userData.username}
                    placeholder="Enter username"
                    type="text"
                    onChange={handleChange}
                    className="w-full bg-slate-800 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-1 block">Role</label>
                  <select
                    name="role"
                    value={userData.role}
                    onChange={handleChange}
                    className="w-full bg-slate-800 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-1 block">Password</label>
                  <input
                    name="password"
                    value={userData.password}
                    placeholder="Enter password"
                    type="password"
                    onChange={handleChange}
                    className="w-full bg-slate-800 border border-slate-600 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors shadow"
                >
                  {loading ? <Loading /> : "Create User"}
                </button>
              </form>
            </div>
          )}

          {/* Users Table */}
          <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm shadow overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-slate-400 text-xs font-semibold uppercase tracking-widest px-6 py-4">#</th>
                  <th className="text-slate-400 text-xs font-semibold uppercase tracking-widest px-6 py-4">Username</th>
                  <th className="text-slate-400 text-xs font-semibold uppercase tracking-widest px-6 py-4">Role</th>
                  <th className="text-slate-400 text-xs font-semibold uppercase tracking-widest px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, idx) => (
                  <tr key={u.username} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 text-slate-500 text-sm">{idx + 1}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-xs uppercase shrink-0">
                          {u.username.charAt(0)}
                        </div>
                        <span className="text-white font-medium text-sm">{u.username}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {editingUser === u.username ? (
                        <select
                          value={editRole}
                          onChange={(e) => setEditRole(e.target.value)}
                          className="bg-slate-800 border border-indigo-500 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                          ))}
                        </select>
                      ) : editingPassword === u.username ? (
                        <input
                          type="password"
                          value={editPassword}
                          onChange={(e) => setEditPassword(e.target.value)}
                          placeholder="New password"
                          className="bg-slate-800 border border-amber-500 text-white text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500 w-44"
                          autoFocus
                        />
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${roleColor(u.role)}`}>
                          <FiShield size={11} /> {u.role}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {u.username !== userProfile.username && (
                        <div className="flex items-center gap-2 justify-end">
                          {editingUser === u.username ? (
                            <>
                              <button
                                onClick={() => handleEditSave(u.username)}
                                disabled={loading}
                                className="flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                              >
                                <FiCheck size={13} /> Save
                              </button>
                              <button
                                onClick={() => setEditingUser(null)}
                                className="flex items-center gap-1 text-xs bg-slate-600 hover:bg-slate-500 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                              >
                                <FiX size={13} /> Cancel
                              </button>
                            </>
                          ) : editingPassword === u.username ? (
                            <>
                              <button
                                onClick={() => handlePasswordSave(u.username)}
                                disabled={loading || !editPassword.trim()}
                                className="flex items-center gap-1 text-xs bg-amber-600 hover:bg-amber-500 disabled:bg-gray-600 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                              >
                                <FiCheck size={13} /> Save
                              </button>
                              <button
                                onClick={() => setEditingPassword(null)}
                                className="flex items-center gap-1 text-xs bg-slate-600 hover:bg-slate-500 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
                              >
                                <FiX size={13} /> Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditStart(u)}
                                className="flex items-center gap-1 text-xs bg-blue-600/20 hover:bg-blue-600 border border-blue-500/40 text-blue-400 hover:text-white px-3 py-1.5 rounded-lg transition-all font-medium"
                              >
                                <FiEdit2 size={13} /> Edit Role
                              </button>
                              <button
                                onClick={() => handlePasswordStart(u)}
                                className="flex items-center gap-1 text-xs bg-amber-600/20 hover:bg-amber-600 border border-amber-500/40 text-amber-400 hover:text-white px-3 py-1.5 rounded-lg transition-all font-medium"
                              >
                                <FiLock size={13} /> Password
                              </button>
                              <button
                                onClick={() => handleDelete(u.username)}
                                disabled={loading}
                                className="flex items-center gap-1 text-xs bg-red-600/20 hover:bg-red-600 border border-red-500/40 text-red-400 hover:text-white px-3 py-1.5 rounded-lg transition-all font-medium"
                              >
                                <FiTrash2 size={13} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <FiUsers className="text-4xl mx-auto mb-3 opacity-40" />
                <p>No users found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AddUser;
